/**
 * Rebuilding a pipeline step's run from the Pipelines editor — one function
 * per save, for every provider mode.
 *
 * The run is rebuilt from an EXPLICIT field list, so the unused mode's fields
 * are cleared and the backend's mode-mixing validator passes. The cost of that
 * shape is that anything left off the list is silently dropped on any edit —
 * which is how `timeoutMs` went missing under provider-set and parallel steps:
 * an admin changing a provider set's temperature also reset the step to the
 * global 120 s per-call timeout. It is now an edited field in every mode.
 */
import type { StepRunConfig } from '../../types/settings';
import type { ReasoningEffort, ReasoningSummaryMode } from '@padcanvas/shared';

/**
 * Params accepted by the row editor's onEditRun callback. The three provider
 * modes are mutually exclusive — exactly one of `provider` / `providerSet` /
 * `parallelSet` is set. The handler clears the unused fields on the underlying
 * StepRunConfig so the backend's mode-mixing validator passes.
 */
export type EditRunParams = {
  // Flat mode
  temperature?: number;
  maxTokens?: number;
  reasoningEffort?: ReasoningEffort;
  thinkingBudgetTokens?: number;
  reasoningSummary?: ReasoningSummaryMode;
  useStrictJsonSchema?: boolean;
  // Tier-aware mode (analysis tier routing — see docs/FeatureDesigns/PipelineInfra/analysis-routing.md §3.2)
  providerSet?: string;
  /** 0-indexed tier reference into the chosen provider set's `levels`. */
  defaultTier?: number;
  maxEscalations?: number;
  // Parallel mode
  parallelSet?: string;
  /** Per-call timeout for this step, in ms — every mode. Undefined ⇒ the
   *  global default. For steps slow by design (one-shot marking, a long paper
   *  read in one call), not to paper over a degraded provider. */
  timeoutMs?: number;
};
export function applyRunParams(run: StepRunConfig, params: EditRunParams): StepRunConfig {
  // A per-run value states what the STEP needs, which does not change with the
  // provider serving it — so it is carried by every mode. Undefined ⇒ the
  // global per-call default (120 s, LLM_CALL_TIMEOUT_MS).
  const timeoutMs = params.timeoutMs;
  if (params.providerSet) {
    return {
      id: run.id,
      promptId: run.promptId,
      isPrimary: run.isPrimary,
      providerSet: params.providerSet,
      defaultTier: params.defaultTier,
      maxEscalations: params.maxEscalations,
      provider: undefined,
      // Both survive a mode switch: a per-run value states what the STEP
      // needs, which does not change with the provider serving it. Wiping
      // them is what forced an operation back onto the provider default.
      temperature: params.temperature,
      maxTokens: params.maxTokens,
      timeoutMs,
      parallelSet: undefined,
      // Carried, not edited here: the gate on the set's other branches.
      ...(run.parallelGate ? { parallelGate: run.parallelGate } : {}),
    };
  }
  if (params.parallelSet) {
    return {
      id: run.id,
      promptId: run.promptId,
      isPrimary: run.isPrimary,
      parallelSet: params.parallelSet,
      provider: undefined,
      temperature: params.temperature,
      maxTokens: params.maxTokens,
      timeoutMs,
      providerSet: undefined,
      defaultTier: undefined,
      maxEscalations: undefined,
      ...(run.parallelGate ? { parallelGate: run.parallelGate } : {}),
    };
  }
  return {
    id: run.id,
    promptId: run.promptId,
    isPrimary: run.isPrimary,
    provider: run.provider,
    temperature: params.temperature,
    maxTokens: params.maxTokens,
    reasoningEffort: params.reasoningEffort,
    thinkingBudgetTokens: params.thinkingBudgetTokens,
    reasoningSummary: params.reasoningSummary,
    useStrictJsonSchema: params.useStrictJsonSchema,
    timeoutMs,
    providerSet: undefined,
    defaultTier: undefined,
    maxEscalations: undefined,
    parallelSet: undefined,
  };
}
